import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import PendingApprovalBackground from '../components/PendingApprovalBackground';
import { motion, useAnimation } from 'framer-motion';
import api from '../services/api';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Mail,
  Phone,
  LogOut,
  RefreshCw,
  Calendar,
  User,
  Shield,
  FileText,
  Sparkles,
  Zap,
  Star
} from 'lucide-react';

const PendingApproval = () => {
  const { user, logout, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState('');
  const [lastChecked, setLastChecked] = useState('');
  const navigate = useNavigate();
  const controls = useAnimation();
  const containerRef = useRef();
  const rawBotUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'FinanceBotSup_bot';
  const botUsername = useMemo(() => rawBotUsername.replace(/^@/, ''), [rawBotUsername]);
  const telegramDeepLink = useMemo(() => {
    return botUsername ? `https://t.me/${botUsername}?start=link` : 'https://t.me';
  }, [botUsername]);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const x = (clientX - width / 2) / width;
      const y = (clientY - height / 2) / height;
      
      controls.start({
        x: x * 20,
        y: y * 20,
        transition: { duration: 0.5, ease: "easeOut" }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, [controls]);

  // Calculate time elapsed since registration
  useEffect(() => {
    if (user?.createdAt) {
      const startTime = new Date(user.createdAt);
      const updateTimeElapsed = () => {
        const now = new Date();
        const diff = now - startTime;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) {
          setTimeElapsed(`${days} ngày ${hours} giờ`);
        } else if (hours > 0) {
          setTimeElapsed(`${hours} giờ ${minutes} phút`);
        } else {
          setTimeElapsed(`${minutes} phút`);
        }
      };
      
      updateTimeElapsed();
      const interval = setInterval(updateTimeElapsed, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [user?.createdAt]);

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = async () => {
    if (isLoading) return; // Prevent multiple clicks
    
    setIsLoading(true);
    toast.loading('Đang kiểm tra trạng thái phê duyệt...', { id: 'refresh-status' });
    
    try {
      // Get token from sessionStorage
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        toast.error('Không tìm thấy token. Vui lòng đăng nhập lại.', { id: 'refresh-status' });
        setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, 2000);
        return;
      }

      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Call real API endpoint /auth/me
      const response = await api.get('/auth/me');
      
      if (response.data.success) {
        const userData = response.data.data;
        const userStatus = userData.status;
        
        if (userStatus === 'approved') {
          // User is approved - redirect based on role
          let redirectPath = '/login';
          let roleName = '';
          
          // Redirect based on user role
          switch (userData.role) {
            case 'admin':
              navigate('/admin');
              roleName = 'Admin Dashboard';
              break;
            case 'seller':
              navigate('/seller');
              roleName = 'DVT_HULO_SYSTEM';
              break;
            case 'designer':
              navigate('/designer');
              roleName = 'Designer Dashboard';
              break;
            default:
              navigate('/login');
              roleName = '';
          }
          
          toast.success(`Tài khoản của bạn đã được phê duyệt! Chuyển hướng đến ${roleName}...`, { id: 'refresh-status' });
          
          // Log approval
          console.log('User approved:', {
            id: userData.id,
            email: userData.email,
            role: userData.role
          });
          
          // Redirect after approval
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 1500);
          
        } else if (userStatus === 'rejected') {
          // User is rejected
          toast.error('Tài khoản của bạn đã bị từ chối. Vui lòng liên hệ admin để biết thêm chi tiết.', { id: 'refresh-status' });
          
          // Log rejection
          
          // Show rejection reason
          setTimeout(() => {
            toast.error('Lý do: Không đủ điều kiện hoặc thông tin không hợp lệ', { duration: 5000 });
          }, 1000);
          
          // Redirect to login page after rejection
          setTimeout(() => {
            logout();
            window.location.href = '/login';
          }, 3000);
          
        } else if (userStatus === 'pending') {
          // User is still pending
          toast.success('Trạng thái: Vẫn đang chờ phê duyệt. Vui lòng kiểm tra lại sau.', { id: 'refresh-status' });
          
          // Log pending status
          
          // Show estimated time
          setTimeout(() => {
            toast.success('Thời gian ước tính: 1-24 giờ làm việc', { duration: 4000 });
          }, 1000);
        }
        
      } else {
        // API returned error
        toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
        console.error('API Error:', response.data);
      }
      
      // Update last checked time
      const now = new Date();
      setLastChecked(now.toLocaleTimeString('vi-VN'));
      
    } catch (error) {
      console.error('Refresh error:', error);
      
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        if (status === 401) {
          // Unauthorized - token expired or invalid
          toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.', { id: 'refresh-status' });
          setTimeout(() => {
            logout();
            window.location.href = '/login';
          }, 2000);
        } else if (status === 403) {
          // Forbidden
          toast.error('Không có quyền truy cập. Vui lòng liên hệ admin.', { id: 'refresh-status' });
        } else {
          toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
        }
      } else if (error.request) {
        // Network error
        toast.error('Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.', { id: 'refresh-status' });
      } else {
        // Other error
        toast.error('Có lỗi xảy ra khi kiểm tra trạng thái. Vui lòng thử lại.', { id: 'refresh-status' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading if auth is still loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900"
    >
      {/* Three.js Background */}
      <div className="fixed inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
          <PendingApprovalBackground />
          <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.5} />
        </Canvas>
      </div>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 z-10">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Floating Orbs */}
      <div className="absolute inset-0 z-10">
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-40 right-32 w-24 h-24 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-25"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-32 left-1/3 w-40 h-40 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-15"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-50 bg-gradient-to-r from-gray-900/80 via-blue-900/80 to-purple-900/80 backdrop-blur-xl shadow-2xl border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <motion.div
                  animate={{ 
                    boxShadow: [
                      "0 0 20px rgba(59, 130, 246, 0.5)",
                      "0 0 40px rgba(147, 51, 234, 0.8)",
                      "0 0 20px rgba(59, 130, 246, 0.5)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-block p-2 rounded-lg bg-white/10 backdrop-blur-sm"
                >
                  <img 
                    src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457007/Dvt_Hulo_logo_copy_t6flpb.png" 
                    alt="DVT Hulo Logo" 
                    className="h-8 w-auto"
                  />
                </motion.div>
              </div>
            </div>
            {user && (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="h-8 w-8 rounded-full border-2 border-white/20"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white/20">
                      <span className="text-sm font-medium text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-300">{user?.email}</p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="flex items-center px-3 py-2 text-sm font-medium text-white rounded-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm border border-white/20"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Đăng xuất</span>
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-20 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-8">
        <motion.div 
          className="max-w-2xl w-full"
          animate={controls}
        >
          <div className="text-center">
            {/* Status Icon with Animation */}
            <motion.div
              initial={{ scale: 0, opacity: 0, rotate: -180 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 1, type: "spring", bounce: 0.6 }}
              className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 mb-8 shadow-2xl relative"
            >
              <motion.div
                animate={{ 
                  boxShadow: [
                    "0 0 20px rgba(251, 191, 36, 0.5)",
                    "0 0 40px rgba(251, 191, 36, 0.8)",
                    "0 0 20px rgba(251, 191, 36, 0.5)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-full"
              />
              <Clock className="h-12 w-12 text-white relative z-10" />
              
              {/* Floating sparkles around icon */}
              <motion.div
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0"
              >
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `rotate(${i * 60}deg) translateY(-20px)`,
                    }}
                    animate={{
                      opacity: [0.5, 1, 0.5],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>

            {/* Status Title with Glitch Effect */}
            <motion.h2
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-5xl font-bold mb-6 relative"
            >
              <motion.span 
                className="block bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{ backgroundSize: "200% 200%" }}
              >
                Tài khoản đang chờ duyệt
              </motion.span>
            </motion.h2>

            {/* Status Message */}
            <motion.p
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-200 mb-8 leading-relaxed"
            >
              Cảm ơn bạn đã đăng ký! Tài khoản của bạn đang được admin xem xét và phê duyệt. 
              Quá trình này thường mất 24 giờ làm việc.
            </motion.p>

            {/* Primary Telegram Link CTA (visible if not linked) */}
            {user && !user?.id_telegram && (
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mb-8"
              >
                <a
                  href={telegramDeepLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold shadow-lg hover:from-sky-600 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2 text-white"><path d="M9.04 16.91l-.39 5.49c.56 0 .81-.24 1.1-.52l2.64-2.53 5.47 4c1 .55 1.7.26 1.97-.93l3.58-16.77v-.01c.32-1.5-.54-2.08-1.53-1.72L1.27 9.73c-1.46.56-1.44 1.36-.25 1.72l5.46 1.7L19.7 5.9c.66-.45 1.26-.2.77.26"/></svg>
                  Liên kết Telegram của tôi
                </a>
                <p className="mt-3 text-sm text-blue-100">
                  Nhấn để mở bot Telegram và nhập lệnh <span className="font-semibold">/link</span> để liên kết nhanh tài khoản.
                  { !botUsername && (
                    <>
                      {' '}Bạn chưa cấu hình tên bot, hệ thống sẽ mở Telegram để bạn tìm bot thủ công.
                    </>
                  )}
                </p>
              </motion.div>
            )}

            {/* Time Elapsed with Animation */}
            {timeElapsed && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl p-6 mb-8 border border-white/20 backdrop-blur-sm"
              >
                <div className="flex items-center justify-center space-x-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  >
                    <Calendar className="h-6 w-6 text-blue-400" />
                  </motion.div>
                  <span className="text-lg font-medium text-white">
                    Đã chờ: {timeElapsed}
                  </span>
                </div>
              </motion.div>
            )}

            {/* User Info Card with Glass Effect */}
            {user ? (
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 mb-8 border border-white/20"
              >
                <div className="flex items-center mb-6">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="h-16 w-16 rounded-full border-4 border-white/20"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center border-4 border-white/20">
                      <span className="text-2xl font-medium text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="ml-6 flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{user?.name}</h3>
                    <p className="text-gray-300 mb-3">{user?.email}</p>
                    {user?.role && (
                      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-white/20">
                        <User className="h-4 w-4 mr-2 text-blue-400" />
                        {user.role === 'seller' ? 'Người bán' : 
                         user.role === 'designer' ? 'Thiết kế' : 
                         user.role === 'admin' ? 'Quản trị viên' : 'Người dùng'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-white/20 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-white">
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <AlertCircle className="h-6 w-6 mr-3 text-yellow-400" />
                      </motion.div>
                      <span className="text-lg">Trạng thái: Chờ phê duyệt</span>
                    </div>
                    <motion.button
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 10px 25px rgba(59, 130, 246, 0.4)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleRefresh}
                      disabled={isLoading}
                      className="flex items-center px-6 py-3 text-white font-medium rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      <RefreshCw className={`h-5 w-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                      {isLoading ? 'Đang kiểm tra...' : 'Làm mới'}
                    </motion.button>
                  </div>
                  {lastChecked && (
                    <div className="mt-4 text-sm text-gray-300">
                      Lần kiểm tra cuối: {lastChecked}
                    </div>
                  )}

                  {/* Telegram link helper if not linked */}
                  {user && !user?.id_telegram && (
                    <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-400/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-blue-200">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-3 text-blue-400"><path d="M9.04 16.91l-.39 5.49c.56 0 .81-.24 1.1-.52l2.64-2.53 5.47 4c1 .55 1.7.26 1.97-.93l3.58-16.77v-.01c.32-1.5-.54-2.08-1.53-1.72L1.27 9.73c-1.46.56-1.44 1.36-.25 1.72l5.46 1.7L19.7 5.9c.66-.45 1.26-.2.77.26"/></svg>
                          <span className="text-sm">
                            Bạn chưa liên kết Telegram. Nhấn để mở bot và nhập lệnh <span className="font-semibold">/link</span>.
                          </span>
                        </div>
                        <a
                          href={telegramDeepLink}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-4 inline-flex items-center px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700"
                        >
                          Mở Telegram
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 mb-8 border border-white/20"
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Không có thông tin người dùng</h3>
                  <p className="text-gray-300">Vui lòng đăng nhập để xem thông tin</p>
                </div>
              </motion.div>
            )}

            {/* What happens next with enhanced design */}
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl p-8 mb-8 border border-white/20 backdrop-blur-sm"
            >
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center justify-center">
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Shield className="h-8 w-8 mr-3 text-blue-400" />
                </motion.div>
                Điều gì sẽ xảy ra tiếp theo?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { icon: CheckCircle, text: "Admin sẽ xem xét thông tin tài khoản của bạn", color: "text-green-400" },
                  { icon: Mail, text: "Bạn sẽ nhận được email thông báo khi được phê duyệt", color: "text-blue-400" },
                  { icon: Zap, text: "Sau khi được phê duyệt, bạn có thể truy cập đầy đủ hệ thống", color: "text-yellow-400" },
                  { icon: Clock, text: "Thời gian xử lý thường từ 1-24 giờ làm việc", color: "text-purple-400" }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.2 + index * 0.1 }}
                    className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 border border-white/10"
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        rotate: [0, 5, 0]
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                      className={`flex-shrink-0 ${item.color}`}
                    >
                      <item.icon className="h-6 w-6" />
                    </motion.div>
                    <span className="text-white text-sm leading-relaxed">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Contact Information with enhanced design */}
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center justify-center">
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <FileText className="h-8 w-8 mr-3 text-green-400" />
                </motion.div>
                Cần hỗ trợ?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center p-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-white/20"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, 0]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Mail className="h-6 w-6 mr-4 text-blue-400" />
                  </motion.div>
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <p className="text-gray-300 text-sm">support@mainsys.com</p>
                  </div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-white/20"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, -10, 0]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                  >
                    <Phone className="h-6 w-6 mr-4 text-green-400" />
                  </motion.div>
                  <div>
                    <p className="text-white font-medium">Điện thoại</p>
                    <p className="text-gray-300 text-sm">+84 123 456 789</p>
                  </div>
                </motion.div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed text-center">
                Nếu bạn cần hỗ trợ khẩn cấp hoặc có thắc mắc về quá trình phê duyệt, 
                vui lòng liên hệ với chúng tôi qua email hoặc điện thoại.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-50 bg-gradient-to-r from-gray-900/80 via-blue-900/80 to-purple-900/80 backdrop-blur-xl border-t border-gray-800/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-300">
            <p>&copy; 2025 DVT Hulo. Tất cả quyền được bảo lưu.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PendingApproval; 
